import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, CreditCard, ChevronRight, Plus, Check, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';

const CheckoutScreen = () => {
  const { user, addAddress, getConfig } = useAuth();
  const { cartItems, subtotal, clearCart } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1=address, 2=review, 3=done
  const [selectedAddress, setSelectedAddress] = useState(
    user?.addresses?.find((a) => a.isDefault)?._id || user?.addresses?.[0]?._id || null
  );
  const [showAddForm, setShowAddForm] = useState(!user?.addresses?.length);
  const [newAddress, setNewAddress] = useState({
    fullName: user?.name || '', phone: '', address: '', city: '', state: '', postalCode: '', country: 'India',
  });
  const [paymentMethod, setPaymentMethod] = useState('UPI');
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discountAmount }
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoCode) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/coupons/validate`,
        { code: promoCode, orderAmount: subtotal },
        getConfig()
      );
      setAppliedPromo(data);
      setPromoError('');
      alert(`Promo code '${data.code}' applied! You get ₹${data.discountAmount} off.`);
    } catch (err) {
      setPromoError(err.response?.data?.message || 'Invalid promo code');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const addresses = await addAddress({ ...newAddress, isDefault: true });
      setSelectedAddress(addresses[addresses.length - 1]._id);
      setShowAddForm(false);
    } catch (err) {
      alert('Failed to save address');
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) return alert('Please select a shipping address');
    const addr = user.addresses.find((a) => a._id === selectedAddress);
    setPlacing(true);
    try {
      const discount = appliedPromo ? appliedPromo.discountAmount : 0;
      const orderData = {
        orderItems: cartItems.map((item) => ({
          name: item.name, qty: item.qty, image: item.image, price: item.price, product: item._id,
        })),
        shippingAddress: {
          address: `${addr.address}, ${addr.city}`,
          city: addr.city,
          postalCode: addr.postalCode,
          country: addr.country,
          phone: addr.phone,
        },
        paymentMethod,
        itemsPrice: subtotal,
        taxPrice: 0,
        shippingPrice: 0,
        promoCode: appliedPromo ? appliedPromo.code : undefined,
        discountPrice: discount,
        totalPrice: subtotal - discount,
      };
      const { data } = await axios.post(`${API_URL}/orders`, orderData, getConfig());
      setOrderId(data._id);

      if (data.paymentSessionId) {
        if (!window.Cashfree) {
          alert('Cashfree SDK not loaded. Please try again.');
          navigate(`/orders/${data._id}`);
          return;
        }

        const cashfree = new window.Cashfree({
          mode: process.env.REACT_APP_CASHFREE_ENV === 'production' ? 'production' : 'sandbox',
        });

        let checkoutOptions = {
          paymentSessionId: data.paymentSessionId,
          redirectTarget: '_modal',
        };

        cashfree.checkout(checkoutOptions).then(async (result) => {
          if (result.error) {
            console.error('Payment error:', result.error);
            alert(result.error.message || 'Payment failed or cancelled.');
            navigate(`/orders/${data._id}`);
          } else {
            // Verify payment
            try {
              setPlacing(true);
              const verifyRes = await axios.put(`${API_URL}/orders/${data._id}/verify`, {}, getConfig());
              if (verifyRes.data.isPaid) {
                clearCart();
                setStep(3);
              } else {
                alert('Payment verification pending. Please check details.');
                navigate(`/orders/${data._id}`);
              }
            } catch (verifyErr) {
              console.error('Verification error:', verifyErr);
              alert('Could not verify payment status.');
              navigate(`/orders/${data._id}`);
            } finally {
              setPlacing(false);
            }
          }
        });
      } else {
        clearCart();
        setStep(3);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  if (cartItems.length === 0 && step !== 3) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-12 space-x-4">
        {[{ n: 1, label: 'Address' }, { n: 2, label: 'Review' }, { n: 3, label: 'Done' }].map((s, i) => (
          <React.Fragment key={s.n}>
            {i > 0 && <div className={`h-0.5 w-16 ${step >= s.n ? 'bg-primary-500' : 'bg-secondary-200'}`} />}
            <div className="flex items-center space-x-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                step >= s.n ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-400'
              }`}>
                {step > s.n ? <Check className="h-5 w-5" /> : s.n}
              </div>
              <span className={`text-sm font-bold ${step >= s.n ? 'text-secondary-900' : 'text-secondary-400'}`}>{s.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Address */}
      {step === 1 && (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-secondary-900 flex items-center space-x-3">
            <MapPin className="h-7 w-7 text-primary-600" />
            <span>Shipping Address</span>
          </h2>

          {user?.addresses?.length > 0 && !showAddForm && (
            <div className="space-y-4">
              {user.addresses.map((addr) => (
                <label key={addr._id} className={`flex items-start space-x-4 p-6 rounded-3xl border-2 cursor-pointer transition-all ${
                  selectedAddress === addr._id ? 'border-primary-500 bg-primary-50' : 'border-secondary-100 hover:border-secondary-200'
                }`}>
                  <input type="radio" name="address" checked={selectedAddress === addr._id}
                    onChange={() => setSelectedAddress(addr._id)} className="mt-1 accent-primary-600" />
                  <div className="flex-1">
                    <p className="font-bold text-secondary-900">{addr.fullName}</p>
                    <p className="text-secondary-600 text-sm">{addr.address}</p>
                    <p className="text-secondary-600 text-sm">{addr.city}, {addr.state} - {addr.postalCode}</p>
                    <p className="text-secondary-500 text-sm">{addr.phone}</p>
                  </div>
                  {addr.isDefault && <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-lg text-[10px] font-black uppercase">Default</span>}
                </label>
              ))}
              <button onClick={() => setShowAddForm(true)} className="w-full border-2 border-dashed border-secondary-200 rounded-3xl p-6 text-secondary-500 hover:border-primary-300 hover:text-primary-600 transition-all flex items-center justify-center space-x-2">
                <Plus className="h-5 w-5" /> <span className="font-bold">Add New Address</span>
              </button>
            </div>
          )}

          {showAddForm && (
            <form onSubmit={handleAddAddress} className="bg-white rounded-3xl border border-secondary-100 p-8 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-secondary-700">Full Name</label>
                  <input type="text" required value={newAddress.fullName} onChange={(e) => setNewAddress({ ...newAddress, fullName: e.target.value })}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-secondary-700">Phone Number</label>
                  <input type="tel" required value={newAddress.phone} onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-secondary-700">Street Address</label>
                <input type="text" required value={newAddress.address} onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                  className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-secondary-700">City</label>
                  <input type="text" required value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-secondary-700">State</label>
                  <input type="text" required value={newAddress.state} onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-secondary-700">PIN Code</label>
                  <input type="text" required value={newAddress.postalCode} onChange={(e) => setNewAddress({ ...newAddress, postalCode: e.target.value })}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
              </div>
              <div className="flex space-x-4">
                <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-2xl font-bold transition-all">Save Address</button>
                {user?.addresses?.length > 0 && (
                  <button type="button" onClick={() => setShowAddForm(false)} className="text-secondary-500 font-bold hover:text-secondary-700">Cancel</button>
                )}
              </div>
            </form>
          )}

          {selectedAddress && !showAddForm && (
            <button onClick={() => setStep(2)} className="w-full bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-bold transition-all flex items-center justify-center space-x-2">
              <span>Continue to Review</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Step 2: Review */}
      {step === 2 && (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-secondary-900">Review Your Order</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-3xl border border-secondary-100 p-6 space-y-4">
                <h3 className="font-bold text-secondary-900 flex items-center space-x-2"><MapPin className="h-5 w-5 text-primary-600" /> <span>Delivery Address</span></h3>
                {(() => {
                  const addr = user?.addresses?.find((a) => a._id === selectedAddress);
                  return addr ? (
                    <div className="text-secondary-600 text-sm space-y-1">
                      <p className="font-bold text-secondary-900">{addr.fullName}</p>
                      <p>{addr.address}, {addr.city}, {addr.state} - {addr.postalCode}</p>
                      <p>{addr.phone}</p>
                    </div>
                  ) : null;
                })()}
                <button onClick={() => setStep(1)} className="text-primary-600 font-bold text-sm">Change</button>
              </div>

              <div className="bg-white rounded-3xl border border-secondary-100 p-6 space-y-4">
                <h3 className="font-bold text-secondary-900 flex items-center space-x-2"><CreditCard className="h-5 w-5 text-primary-600" /> <span>Payment Method</span></h3>
                <div className="flex flex-wrap gap-3">
                  {['UPI', 'Card'].map((m) => (
                    <button key={m} onClick={() => setPaymentMethod(m)}
                      className={`px-6 py-3 rounded-2xl font-bold text-sm transition-all ${paymentMethod === m ? 'bg-primary-600 text-white' : 'bg-secondary-50 text-secondary-600 border border-secondary-200'}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-secondary-100 p-6 space-y-4">
                <h3 className="font-bold text-secondary-900">Order Items</h3>
                {cartItems.map((item) => (
                  <div key={item._id} className="flex items-center space-x-4 py-3 border-b border-secondary-50 last:border-0">
                    <img src={item.image} alt={item.name} className="w-16 h-16 rounded-xl object-cover" />
                    <div className="flex-1">
                      <p className="font-bold text-secondary-900">{item.name}</p>
                      <p className="text-sm text-secondary-500">{item.qty} × ₹{item.price}</p>
                    </div>
                    <p className="font-bold text-secondary-900">₹{item.qty * item.price}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-secondary-900 text-white rounded-[32px] p-8 sticky top-28 space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-6">Order Total</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-secondary-300">
                      <span>Items</span>
                      <span>₹{subtotal}</span>
                    </div>
                    {appliedPromo && (
                      <div className="flex justify-between text-red-400 font-bold">
                        <span>Discount ({appliedPromo.code})</span>
                        <span>-₹{appliedPromo.discountAmount}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-secondary-300">
                      <span>Shipping</span>
                      <span className="text-primary-400 font-bold">FREE</span>
                    </div>
                    <div className="pt-3 border-t border-secondary-800 flex justify-between">
                      <span className="text-lg font-bold">Total</span>
                      <span className="text-xl font-bold text-primary-400">
                        ₹{subtotal - (appliedPromo ? appliedPromo.discountAmount : 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Promo Code Input */}
                <div className="pt-4 border-t border-secondary-800 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-secondary-400">Promo Code</h4>
                  {appliedPromo ? (
                    <div className="flex items-center justify-between bg-secondary-800 px-4 py-2 rounded-xl text-sm">
                      <span className="font-mono font-bold text-primary-400">{appliedPromo.code} Applied</span>
                      <button onClick={handleRemovePromo} className="text-xs text-red-400 font-bold hover:underline">Remove</button>
                    </div>
                  ) : (
                    <form onSubmit={handleApplyPromo} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter Code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        className="bg-secondary-800 border-none rounded-xl px-3 py-2 text-sm w-full focus:ring-1 focus:ring-primary-500 outline-none uppercase font-mono font-bold text-white"
                      />
                      <button
                        type="submit"
                        disabled={promoLoading || !promoCode}
                        className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all"
                      >
                        {promoLoading ? '...' : 'Apply'}
                      </button>
                    </form>
                  )}
                  {promoError && <p className="text-xs text-red-400 font-bold">{promoError}</p>}
                </div>

                <button onClick={handlePlaceOrder} disabled={placing}
                  className="w-full bg-primary-500 hover:bg-primary-400 text-white py-4 rounded-2xl font-bold transition-all disabled:opacity-50">
                  {placing ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && (
        <div className="text-center py-20 space-y-6">
          <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-secondary-900">Order Placed!</h2>
          <p className="text-secondary-600 max-w-md mx-auto">Your order has been placed successfully. We'll deliver it to your doorstep soon.</p>
          {orderId && <p className="text-sm text-secondary-400">Order ID: <span className="font-mono font-bold">{orderId}</span></p>}
          <div className="flex justify-center space-x-4 pt-4">
            <button onClick={() => navigate('/orders')} className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-2xl font-bold transition-all flex items-center space-x-2">
              <Truck className="h-5 w-5" /> <span>View Orders</span>
            </button>
            <button onClick={() => navigate('/')} className="bg-white border border-secondary-200 text-secondary-700 px-8 py-3 rounded-2xl font-bold transition-all">Continue Shopping</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutScreen;
